import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Building2, Plus, ChevronRight, ArrowLeft, CheckCircle2, AlertTriangle,
  Search, Edit3, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Select, Spinner, EmptyState,
} from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import SupplierMoneyCard from "./SupplierMoneyCard";

// T-315 vendor register — vendor type taxonomy.
const VENDOR_TYPES = [
  { v: "general",       label: "Supplier" },
  { v: "subcontractor", label: "Subcontractor" },
  { v: "plant_hire",    label: "Plant hire" },
  { v: "labour_hire",   label: "Labour hire" },
  { v: "structure",     label: "Structure / tower owner" },
] as const;
const vendorTypeLabel = (v: string) => VENDOR_TYPES.find(t => t.v === v)?.label ?? v ?? "Supplier";

// =============================================================================
// Suppliers — list + new + detail
// Schema: suppliers uses uuid PK `supplier_id` (not int `id`).
// Real columns: supplier_id, supplier_code, legal_name, trading_name, abn,
//   is_gst_registered, primary_contact_name/email/phone, billing_email,
//   address_line1/2, suburb, state, postcode, payment_terms_days,
//   xero_contact_id, is_active, is_approved, notes
// =============================================================================

export function SuppliersListScreen() {
  const user = useRequireAuth();
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");

  const { data: suppliers = [], isLoading, error } = useQuery({
    queryKey: ["suppliers_list", search, onlyActive, typeFilter],
    queryFn: async () => {
      let q = supabase
        .from("suppliers")
        .select("supplier_id, supplier_code, legal_name, trading_name, abn, is_gst_registered, payment_terms_days, billing_email, is_active, is_approved, supplier_kind")
        .order("legal_name", { ascending: true })
        .limit(500);
      if (onlyActive) q = q.eq("is_active", true);
      if (typeFilter) q = q.eq("supplier_kind", typeFilter);
      if (search.trim()) q = q.or(`legal_name.ilike.%${search}%,trading_name.ilike.%${search}%,abn.ilike.%${search}%,supplier_code.ilike.%${search}%`);
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Suppliers</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Vendors, subbies, plant hire. Used on POs, AP invoices, and for Xero sync.
          </div>
        </div>
        {hasRole(user, "manager") && (
          <Link to="/suppliers/new"><Button icon={Plus}>New supplier</Button></Link>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 max-w-md">
          <Search size={14} className="text-text-muted"/>
          <input
            placeholder="Search name, code, or ABN..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1"
          />
        </div>
        <button
          onClick={() => setOnlyActive(!onlyActive)}
          className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
            onlyActive ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line"
          }`}
        >
          {onlyActive ? "Active only" : "Including archived"}
        </button>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 rounded text-[12px] bg-ink-raised text-text border border-line outline-none"
        >
          <option value="">All types</option>
          {VENDOR_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
      </div>

      {error && (
        <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">
          <AlertTriangle size={12} className="inline mr-1.5"/>
          {(error as any).message}
        </div>
      )}

      {isLoading ? <div className="py-10 flex justify-center"><Spinner/></div>
       : suppliers.length === 0 ? <Card><EmptyState title="No suppliers" description="Add your first supplier to start issuing POs." icon={Building2}/></Card>
       : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-line">
                  {["Code", "Name", "Type", "ABN", "Terms", "Billing email", "GST", "Status", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {suppliers.map((s, i) => (
                    <tr key={s.supplier_id} className={`border-b border-line cursor-pointer hover:bg-ink-hover ${i % 2 ? "bg-ink/40" : ""}`}>
                      <td className="px-4 py-3 font-mono text-[11px] text-text-muted">{s.supplier_code ?? "—"}</td>
                      <td className="px-4 py-3">
                        <Link to={`/suppliers/${s.supplier_id}`} className="text-text hover:text-accent">
                          {s.legal_name}
                          {s.trading_name && s.trading_name !== s.legal_name && (
                            <span className="text-text-muted text-[12px] ml-1.5">({s.trading_name})</span>
                          )}
                        </Link>
                      </td>
                      <td className="px-4 py-3"><Pill label={vendorTypeLabel(s.supplier_kind)} tone="default" small/></td>
                      <td className="px-4 py-3 font-mono text-[11px] text-text-muted">{s.abn ?? <span className="text-text-dim">—</span>}</td>
                      <td className="px-4 py-3 font-mono text-[12px]">{s.payment_terms_days ? `${s.payment_terms_days}d` : "—"}</td>
                      <td className="px-4 py-3 text-[12px] text-text-muted">{s.billing_email ?? "—"}</td>
                      <td className="px-4 py-3"><Pill label={s.is_gst_registered ? "GST" : "no GST"} tone={s.is_gst_registered ? "pending" : "default"} small/></td>
                      <td className="px-4 py-3">
                        <Pill label={s.is_active ? "active" : "archived"} tone={s.is_active ? "won" : "lost"} small/>
                        {s.is_approved === false && <Pill label="pending approval" tone="warning" small/>}
                      </td>
                      <td className="px-4 py-3"><ChevronRight size={14} className="text-text-dim"/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
    </div>
  );
}

// =============================================================================
// Supplier detail / edit / new
// =============================================================================

export function SupplierDetailScreen() {
  const { supplierId } = useParams<{ supplierId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const isNew = supplierId === "new";

  const { data: supplier, isLoading } = useQuery({
    queryKey: ["supplier", supplierId],
    enabled: !!supplierId && !isNew,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .eq("supplier_id", supplierId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Recent POs to this supplier
  const { data: recentPos = [] } = useQuery({
    queryKey: ["supplier_pos", supplierId],
    enabled: !!supplierId && !isNew,
    queryFn: async () => {
      const { data } = await supabase
        .from("purchase_orders")
        .select("po_id, po_number, po_title, status, po_date, total_inc_gst")
        .eq("supplier_id", supplierId!)
        .order("po_date", { ascending: false })
        .limit(10);
      return (data ?? []) as any[];
    },
  });

  // T-384 — this supplier's rate cards (SoR). The rates world existed but was
  // invisible from the supplier record; this panel is the missing joint.
  const { data: rateCards = [] } = useQuery({
    queryKey: ["supplier_rate_cards", supplierId],
    enabled: !!supplierId && !isNew,
    queryFn: async () => {
      const { data } = await supabase
        .from("rate_cards")
        .select("rate_card_id, rate_card_code, display_name, is_active, effective_from, effective_until, updated_at")
        .eq("supplier_id", supplierId!)
        .eq("rate_card_type", "supplier_sor")
        .order("is_active", { ascending: false })
        .order("effective_from", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  // T-315 P2 — compliance: insurances against this vendor + linked subbie record
  const { data: insurances = [] } = useQuery({
    queryKey: ["vendor_insurances", supplierId],
    enabled: !!supplierId && !isNew,
    queryFn: async () => {
      const { data } = await supabase.from("vendor_insurances")
        .select("*").eq("supplier_id", supplierId!).order("expiry_date", { ascending: true });
      return (data ?? []) as any[];
    },
  });
  const { data: linkedSubbie } = useQuery({
    queryKey: ["supplier_linked_subbie", supplierId],
    enabled: !!supplierId && !isNew,
    queryFn: async () => {
      const { data } = await supabase.from("subcontractor_companies")
        .select("subcontractor_company_id, company_code, legal_name, is_approved")
        .eq("supplier_id", supplierId!).maybeSingle();
      return data as any;
    },
  });
  const [insEditing, setInsEditing] = useState<any | null>(null);
  const archiveMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("suppliers").update({ is_active: false }).eq("supplier_id", supplierId!);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["supplier"] });
      qc.invalidateQueries({ queryKey: ["suppliers_list"] });
      navigate("/suppliers");
    },
    onError: (e: any) => alert(`Couldn't archive the supplier: ${e?.message ?? e}`),   // T-403
  });
  const saveInsMut = useMutation({
    mutationFn: async (d: any) => {
      let certPath = d.certificate_file_path ?? null;
      if (d._file) {
        try {
          const safe = d._file.name.replace(/[^\w.\-]/g, "_");
          const key = `vendor-compliance/${supplierId}/${Date.now()}-${safe}`;
          const { error } = await supabase.storage.from("ap-documents").upload(key, d._file, { upsert: false });
          if (!error) certPath = key;
        } catch { /* non-fatal — keep the record even if the cert upload fails */ }
      }
      const payload: any = {
        supplier_id: supplierId,
        policy_type: d.policy_type,
        insurer: d.insurer?.trim() || null,
        policy_number: d.policy_number?.trim() || null,
        cover_amount: d.cover_amount ? Number(d.cover_amount) : null,
        effective_from: d.effective_from || null,
        expiry_date: d.expiry_date || null,
        certificate_file_path: certPath,
        notes: d.notes?.trim() || null,
      };
      if (d.insurance_id) {
        const { error } = await supabase.from("vendor_insurances").update(payload).eq("insurance_id", d.insurance_id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("vendor_insurances").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor_insurances", supplierId] }); setInsEditing(null); },
    onError: (e: any) => alert(`Insurance save failed: ${e?.message ?? String(e)}`),
  });

  const [form, setForm] = useState<any>(null);
  React.useEffect(() => {
    if (isNew) {
      setForm({
        // supplier_code intentionally omitted — DB default mints SUP-NNNNN
        // on insert via app.next_counter (migration 056).
        legal_name: "", trading_name: "", abn: "",
        is_gst_registered: true, payment_terms_days: 30, supplier_kind: "general",
        primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "",
        billing_email: "",
        address_line1: "", address_line2: "", suburb: "", state: "QLD", postcode: "",
        is_active: true, is_approved: false, notes: "",
      });
      setEditing(true);
    } else if (supplier && !form) {
      setForm(supplier);
    }
  }, [supplier, isNew]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form?.legal_name?.trim()) throw new Error("Legal name is required");

      const payload: any = {
        // supplier_code: never set from app code — DB default auto-mints
        // SUP-NNNNN on insert (migration 056). On update, omitting it leaves
        // the existing value alone.
        legal_name:             form.legal_name.trim(),
        trading_name:           form.trading_name?.trim() || null,
        abn:                    form.abn?.trim() || null,
        is_gst_registered:      !!form.is_gst_registered,
        supplier_kind:            form.supplier_kind || "general",
        payment_terms_days:     form.payment_terms_days ? Number(form.payment_terms_days) : null,
        primary_contact_name:   form.primary_contact_name?.trim() || null,
        primary_contact_email:  form.primary_contact_email?.trim() || null,
        primary_contact_phone:  form.primary_contact_phone?.trim() || null,
        billing_email:          form.billing_email?.trim() || null,
        address_line1:          form.address_line1?.trim() || null,
        address_line2:          form.address_line2?.trim() || null,
        suburb:                 form.suburb?.trim() || null,
        state:                  form.state?.trim() || null,
        postcode:               form.postcode?.trim() || null,
        notes:                  form.notes?.trim() || null,
        is_active:              !!form.is_active,
        is_approved:            !!form.is_approved,
      };

      if (isNew) {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        payload.created_by = authUser?.id ?? null;
        payload.updated_by = authUser?.id ?? null;
        const { data, error } = await supabase.from("suppliers").insert(payload).select("supplier_id").single();
        if (error) throw error;
        return data.supplier_id;
      } else {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        payload.updated_by = authUser?.id ?? null;
        const { error } = await supabase.from("suppliers").update(payload).eq("supplier_id", supplierId!);
        if (error) throw error;
        return supplierId!;
      }
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ["supplier"] });
      qc.invalidateQueries({ queryKey: ["suppliers_list"] });
      setEditing(false);
      if (isNew) navigate(`/suppliers/${id}`, { replace: true });
    },
  });

  if (!isNew && isLoading) return <div className="py-20 flex justify-center"><Spinner/></div>;
  if (!isNew && !supplier) return <div className="p-6">Supplier not found.</div>;
  if (!form) return <div className="py-20 flex justify-center"><Spinner/></div>;

  const canEdit = hasRole(user, "manager");
  const showCompliance = !isNew && ["subcontractor", "plant_hire", "labour_hire"].includes(form.supplier_kind);

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6">
      <button onClick={() => navigate("/suppliers")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Suppliers
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight">
            {isNew ? "New supplier" : form.legal_name}
          </h1>
          {!isNew && (
            <div className="text-[12px] text-text-muted mt-1">
              {form.supplier_code && <span className="font-mono">{form.supplier_code} · </span>}
              {form.abn ? `ABN ${form.abn}` : "No ABN"}
              {" · "}
              {form.is_active ? "active" : "archived"}
              {form.is_approved === false && " · pending approval"}
            </div>
          )}
        </div>
        {canEdit && !isNew && (
          <div className="flex items-center gap-2">
            {!editing && <Button icon={Edit3} variant="secondary" onClick={() => setEditing(true)}>Edit</Button>}
            {!editing && form.is_active && (
              <Button variant="secondary" onClick={() => { if (confirm("Archive this vendor? It drops off the active list — no data is lost, and you can re-activate via Edit.")) archiveMut.mutate(); }}>
                Archive
              </Button>
            )}
          </div>
        )}
      </div>

      {/* T-464 — money summary up top; contact/details below */}
      {!isNew && <SupplierMoneyCard supplierId={supplierId!} />}

      <Card>
        <div className="p-5 space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Supplier code" hint="Optional short reference code">
              <Input value={form.supplier_code ?? ""} disabled={!editing} mono onChange={e => setForm({...form, supplier_code: e.target.value})}/>
            </Field>
            <Field label="ABN" hint="11-digit ABN">
              <Input value={form.abn ?? ""} disabled={!editing} mono onChange={e => setForm({...form, abn: e.target.value})}/>
            </Field>
          </div>
          <Field label="Vendor type" hint="Subcontractor + plant-hire can carry compliance. Card-receipt stores (Bunnings, fuel) live in Brand mapping, not here.">
            <Select value={form.supplier_kind ?? "general"} disabled={!editing} onChange={e => setForm({...form, supplier_kind: e.target.value})}>
              {VENDOR_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </Select>
          </Field>
          <Field label="Legal name *">
            <Input value={form.legal_name ?? ""} disabled={!editing} onChange={e => setForm({...form, legal_name: e.target.value})}/>
          </Field>
          <Field label="Trading name" hint="If different from legal name">
            <Input value={form.trading_name ?? ""} disabled={!editing} onChange={e => setForm({...form, trading_name: e.target.value})}/>
          </Field>

          {/* Contact */}
          <div className="pt-3 border-t border-line">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-3">Primary contact</div>
            <Field label="Contact name">
              <Input value={form.primary_contact_name ?? ""} disabled={!editing} onChange={e => setForm({...form, primary_contact_name: e.target.value})}/>
            </Field>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Contact email">
                <Input value={form.primary_contact_email ?? ""} disabled={!editing} onChange={e => setForm({...form, primary_contact_email: e.target.value})}/>
              </Field>
              <Field label="Contact phone">
                <Input value={form.primary_contact_phone ?? ""} disabled={!editing} onChange={e => setForm({...form, primary_contact_phone: e.target.value})}/>
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Billing / accounts email" hint="Where to send POs and remittance advices">
                <Input value={form.billing_email ?? ""} disabled={!editing} onChange={e => setForm({...form, billing_email: e.target.value})}/>
              </Field>
            </div>
          </div>

          {/* Address */}
          <div className="pt-3 border-t border-line">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-3">Address</div>
            <Field label="Street">
              <Input value={form.address_line1 ?? ""} disabled={!editing} onChange={e => setForm({...form, address_line1: e.target.value})}/>
            </Field>
            <Field label="Address line 2">
              <Input value={form.address_line2 ?? ""} disabled={!editing} onChange={e => setForm({...form, address_line2: e.target.value})}/>
            </Field>
            <div className="grid grid-cols-[1fr_100px_100px] gap-3 mt-3">
              <Field label="Suburb">
                <Input value={form.suburb ?? ""} disabled={!editing} onChange={e => setForm({...form, suburb: e.target.value})}/>
              </Field>
              <Field label="State">
                <Input value={form.state ?? ""} disabled={!editing} onChange={e => setForm({...form, state: e.target.value})}/>
              </Field>
              <Field label="Postcode">
                <Input value={form.postcode ?? ""} disabled={!editing} mono onChange={e => setForm({...form, postcode: e.target.value})}/>
              </Field>
            </div>
          </div>

          {/* Terms + flags */}
          <div className="pt-3 border-t border-line">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Payment terms (days)">
                <Input type="number" value={form.payment_terms_days ?? ""} disabled={!editing} mono onChange={e => setForm({...form, payment_terms_days: e.target.value})}/>
              </Field>
            </div>
            <div className="flex items-center gap-5 pt-3">
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" disabled={!editing} checked={!!form.is_gst_registered} onChange={e => setForm({...form, is_gst_registered: e.target.checked})}/>
                GST registered
              </label>
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" disabled={!editing} checked={!!form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}/>
                Active
              </label>
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" disabled={!editing} checked={!!form.is_approved} onChange={e => setForm({...form, is_approved: e.target.checked})}/>
                Approved
              </label>
            </div>
          </div>

          {/* Notes */}
          <div className="pt-3 border-t border-line">
            <Field label="Notes">
              <textarea
                value={form.notes ?? ""}
                disabled={!editing}
                onChange={e => setForm({...form, notes: e.target.value})}
                className="w-full bg-ink border border-line rounded p-2 text-[13px] text-text min-h-[70px] outline-none focus:border-accent/40 disabled:opacity-60"
              />
            </Field>
          </div>

          {saveMut.isError && (
            <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
              <AlertTriangle size={11} className="inline mr-1"/>
              {(saveMut.error as any)?.message ?? String(saveMut.error)}
            </div>
          )}

          {editing && (
            <div className="flex justify-end gap-2 pt-2 border-t border-line">
              {!isNew && <Button variant="secondary" onClick={() => { setForm(supplier); setEditing(false); }}>Cancel</Button>}
              {isNew && <Button variant="secondary" onClick={() => navigate("/suppliers")}>Cancel</Button>}
              <Button icon={CheckCircle2} onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                {saveMut.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* T-381 — for subbie-linked suppliers, compliance is MANAGED on the
          subbie record. This section becomes a pointer, not an editor: one home
          for insurance, no split-brain. Pure vendors keep the editor. */}
      {showCompliance && linkedSubbie && (
        <Card>
          <div className="p-5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-text-muted">
              <span className="text-[11px] uppercase tracking-wider2 font-semibold block mb-1">Compliance &amp; insurances</span>
              Managed on the subcontractor record ({insurances.length} polic{insurances.length === 1 ? "y" : "ies"} on file) — one home for certs, workers + approvals.
            </div>
            <Link to={`/subbies/${linkedSubbie.subcontractor_company_id}`}>
              <Button size="sm" variant="secondary">Open subbie record</Button>
            </Link>
          </div>
        </Card>
      )}
      {showCompliance && !linkedSubbie && (
        <Card>
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Compliance &amp; insurances</div>
              {canEdit && <Button size="sm" icon={Plus} onClick={() => setInsEditing({ policy_type: "public_liability" })}>Add insurance</Button>}
            </div>
            {insurances.length === 0 ? (
              <div className="text-[12px] text-text-dim">No insurances on file. Add public liability, workers comp, professional indemnity, etc.</div>
            ) : (
              <div className="space-y-1.5">
                {insurances.map(ins => {
                  const exp = ins.expiry_date ? new Date(ins.expiry_date) : null;
                  const days = exp ? Math.round((exp.getTime() - Date.now()) / 86400000) : null;
                  const tone = days == null ? "default" : days < 0 ? "lost" : days < 30 ? "warning" : "won";
                  return (
                    <div key={ins.insurance_id} className="px-3 py-2 rounded border border-line flex items-center gap-3 text-[12px]">
                      <span className="text-text capitalize">{(ins.policy_type ?? "").replace(/_/g, " ")}</span>
                      {ins.insurer && <span className="text-text-muted">{ins.insurer}</span>}
                      {ins.cover_amount && <span className="font-mono text-text-muted">${Number(ins.cover_amount).toLocaleString("en-AU")}</span>}
                      <span className="flex-1"/>
                      {ins.certificate_file_path && <span className="text-[10px] text-accent">cert ✓</span>}
                      {ins.expiry_date && <Pill label={`exp ${ins.expiry_date}${days != null && days < 0 ? " (expired)" : days != null && days < 30 ? ` · ${days}d` : ""}`} tone={tone as any} small/>}
                      {canEdit && <button onClick={() => setInsEditing(ins)} className="text-text-muted hover:text-text"><Edit3 size={12}/></button>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* T-381 — supplier ↔ subbie cross-link. If their people work on our
          sites, this is where the supplier grows its subbie hat (prefilled). */}
      {!isNew && (
        <Card className="mb-4">
          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-0.5">Subcontractor profile</div>
              <div className="text-[12px] text-text-muted">
                {linkedSubbie
                  ? <>This supplier works on site as <span className="text-text font-medium">{linkedSubbie.legal_name}</span>{linkedSubbie.is_approved ? " (approved)" : " (not yet approved)"}.</>
                  : "Do their people work on our sites (wet hire, trades)? Give them a subbie profile — compliance, workers' tickets + client approvals."}
              </div>
            </div>
            {linkedSubbie ? (
              <Link to={`/subbies/${linkedSubbie.subcontractor_company_id}`}>
                <Button size="sm" variant="secondary">Open subbie record</Button>
              </Link>
            ) : (
              <Button size="sm" variant="secondary" onClick={async () => {
                if (!supplier) return;
                const s: any = supplier;
                const base = (s.supplier_code || s.legal_name || "SUBBIE").toString().toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 16);
                // Try the supplier code as company code; suffix on collision.
                for (const code of [base, `${base}-SUB`, `${base}-${Date.now().toString(36).slice(-4).toUpperCase()}`]) {
                  const { data, error } = await supabase.from("subcontractor_companies").insert({
                    company_code: code,
                    legal_name: s.legal_name,
                    trading_name: s.trading_name ?? null,
                    abn: s.abn ?? null,
                    primary_contact_name: s.primary_contact_name ?? null,
                    primary_contact_email: s.primary_contact_email ?? null,
                    primary_contact_phone: s.primary_contact_phone ?? null,
                    address_line1: s.address_line1 ?? null, address_line2: s.address_line2 ?? null,
                    suburb: s.suburb ?? null, state: s.state ?? null, postcode: s.postcode ?? null,
                    supplier_id: s.supplier_id,
                  }).select("subcontractor_company_id").single();
                  if (!error && data) { navigate(`/subbies/${(data as any).subcontractor_company_id}`); return; }
                  if (error && !/duplicate|unique/i.test(error.message)) { window.alert(`Couldn't create: ${error.message}`); return; }
                }
                window.alert("Couldn't find a free company code — create it manually via New subbie.");
              }}>Create subbie profile</Button>
            )}
          </div>
        </Card>
      )}

      {/* T-384 — Rate cards (SoR) for this supplier: see, open, add, import. */}
      {!isNew && (
        <Card className="mb-4">
          <div className="p-5">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Rate cards (SoR)</div>
              <div className="flex gap-2">
                <Link to="/rates/import-pdf"><Button size="sm" variant="secondary">Import rates PDF</Button></Link>
                <Link to="/rates/new"><Button size="sm" variant="secondary">New card</Button></Link>
              </div>
            </div>
            {rateCards.length === 0 ? (
              <div className="text-[12px] text-text-muted">
                No rate card loaded for this supplier — import their SoR PDF or create one, and their
                lines become pickable on the SUPPLIER tab in the quote builder.
              </div>
            ) : (
              <div className="space-y-1.5">
                {rateCards.map((rc: any) => (
                  <Link key={rc.rate_card_id} to={`/rates/${rc.rate_card_id}`}>
                    <div className="px-3 py-2 rounded border border-line hover:border-accent/30 hover:bg-ink-hover transition-colors flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-[11px] text-accent">{rc.rate_card_code}</span>
                      <span className="text-[13px] text-text flex-1 min-w-0 truncate">{rc.display_name}</span>
                      <Pill label={rc.is_active ? "active" : "inactive"} tone={rc.is_active ? "won" : "lost"} small/>
                      <span className="text-[11px] text-text-muted font-mono">
                        {rc.effective_from ? `from ${fmtDate(rc.effective_from)}` : ""}{rc.effective_until ? ` to ${fmtDate(rc.effective_until)}` : ""}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {!isNew && recentPos.length > 0 && (
        <Card>
          <div className="p-5">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-3">Recent POs</div>
            <div className="space-y-1.5">
              {recentPos.map(po => (
                <Link key={po.po_id} to={`/pos/${po.po_id}`}>
                  <div className="px-3 py-2 rounded border border-line hover:border-accent/30 hover:bg-ink-hover transition-colors flex items-center gap-3">
                    <span className="font-mono text-[11px] text-accent">{po.po_number}</span>
                    <Pill label={po.status} tone={po.status === "paid" ? "won" : po.status === "sent" ? "mobilising" : "pending"} small/>
                    <span className="flex-1 truncate text-[12px]">{po.po_title}</span>
                    <span className="font-mono text-[12px] text-text-muted">${Number(po.total_inc_gst ?? 0).toLocaleString("en-AU", {minimumFractionDigits: 2})}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Card>
      )}

      {insEditing && (
        <InsuranceEditor d={insEditing} onClose={() => setInsEditing(null)} onSave={(x: any) => saveInsMut.mutate(x)} saving={saveInsMut.isPending}/>
      )}
    </div>
  );
}

// T-315 P2 — add/edit one insurance policy held against a vendor.
function InsuranceEditor({ d, onClose, onSave, saving }: any) {
  const [s, setS] = useState<any>(d);
  const set = (k: string, v: any) => setS({ ...s, [k]: v });
  const POLICY_TYPES = [
    { v: "public_liability",        label: "Public liability" },
    { v: "workers_comp",            label: "Workers compensation" },
    { v: "professional_indemnity",  label: "Professional indemnity" },
    { v: "plant_all_risk",          label: "Plant / all-risk" },
    { v: "motor",                   label: "Motor" },
    { v: "other",                   label: "Other" },
  ];
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-ink-card border border-line w-full md:max-w-md md:rounded-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-center justify-between">
          <h3 className="text-lg font-semibold">{d.insurance_id ? "Edit" : "Add"} insurance</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Policy type *">
            <Select value={s.policy_type ?? "public_liability"} onChange={e => set("policy_type", e.target.value)}>
              {POLICY_TYPES.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Insurer"><Input value={s.insurer ?? ""} onChange={e => set("insurer", e.target.value)}/></Field>
            <Field label="Policy number"><Input value={s.policy_number ?? ""} mono onChange={e => set("policy_number", e.target.value)}/></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Cover ($)"><Input type="number" mono value={s.cover_amount ?? ""} onChange={e => set("cover_amount", e.target.value)}/></Field>
            <Field label="Effective"><Input type="date" mono value={s.effective_from ?? ""} onChange={e => set("effective_from", e.target.value)}/></Field>
            <Field label="Expiry"><Input type="date" mono value={s.expiry_date ?? ""} onChange={e => set("expiry_date", e.target.value)}/></Field>
          </div>
          <Field label="Certificate (optional)" hint="PDF/image — stored against this vendor.">
            <input type="file" accept="application/pdf,image/*" onChange={e => set("_file", e.target.files?.[0] ?? null)} className="text-[12px] text-text-muted"/>
            {s.certificate_file_path && !s._file && <div className="text-[10px] text-accent mt-1">Current cert on file.</div>}
          </Field>
          <Field label="Notes"><Input value={s.notes ?? ""} onChange={e => set("notes", e.target.value)}/></Field>
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={CheckCircle2} disabled={!s.policy_type || saving} onClick={() => onSave(s)}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}
